import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { IconChevronRight } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'

interface Props {
  driveId: string
  projectId: string
}

export default function ProjectSelectionsTab({ driveId, projectId }: Props) {
  const navigate = useNavigate()
  const { getDriveSelections } = useClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['drive', driveId, 'selections'],
    queryFn: async () => {
      const res = await getDriveSelections(driveId)
      return res.data
    },
    enabled: !!driveId,
  })

  if (isLoading) {
    return (
      <div className='bg-neutral p-4 border border-[var(--portal-border)]'>
        <p className='text-sm opacity-60'>Lade Auswahlen...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='bg-neutral p-4 border border-[var(--portal-border)]'>
        <p className='text-error'>Fehler beim Laden der Auswahlen</p>
      </div>
    )
  }

  const selections = data || []

  if (selections.length === 0) {
    return (
      <div className='bg-neutral p-6 border border-[var(--portal-border)] text-center'>
        <p className='opacity-60'>Noch keine Auswahlen eingegangen.</p>
        <p className='text-sm opacity-50 mt-1'>
          Gäste können in der Galerie Bilder auswählen und an dich senden.
        </p>
      </div>
    )
  }

  return (
    <div className='bg-neutral border border-[var(--portal-border)]'>
      <table className='table w-full'>
        <thead>
          <tr>
            <th className='text-xs uppercase tracking-wider opacity-60 font-semibold'>Datum</th>
            <th className='text-xs uppercase tracking-wider opacity-60 font-semibold'>Name</th>
            <th className='text-xs uppercase tracking-wider opacity-60 font-semibold'>Email</th>
            <th className='text-xs uppercase tracking-wider opacity-60 font-semibold'>Alben</th>
            <th className='text-xs uppercase tracking-wider opacity-60 font-semibold'>Bilder</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {selections.map(sel => {
            const date = new Date(sel.created_at).toLocaleDateString('de-DE', {
              day: 'numeric', month: 'short', year: 'numeric',
            })
            return (
              <tr
                key={sel.id}
                className='hover:bg-secondary group cursor-pointer'
                onClick={() => navigate(`/portal/projects/${projectId}/selections/${sel.id}`)}
              >
                <td className='opacity-70'>{date}</td>
                <td>{sel.name || <span className='opacity-50'>—</span>}</td>
                <td>{sel.email || <span className='opacity-50'>—</span>}</td>
                <td>{sel.selection_albums.length}</td>
                <td>{sel.files_count}</td>
                <td className='text-right'>
                  <IconChevronRight size={16} className='opacity-50 group-hover:opacity-100 inline-block' />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
